'use strict';
/* ==========================================================================
   core-local/circlekit.js — "Round and Round": the drawing kit (global CK). Every round thing in the lesson, drawn in
   notebook ink by code: the bike wheel, the shelf discs and job shapes, the pizza and its slices, the floor strip with
   its stamps and track, the across sticks, radius squares, the unit grid, the lab table, chips, levers, readouts.
   API contract: core-local/CIRCLEKIT.md. Gallery + checks: tools/circlekit/ (node lessons/circles/tools/circlekit/qa_circlekit.js).

   Rules every piece follows
   - EXACT GEOMETRY: a circle of radius R is exactly 2R wide; every ink line of an outline lies INSIDE the shape (a
     wheel touches the floor at y, a slice's pixels fill exactly Circle.strip(r, n).box × k). Model points map as
     (ox + x·k, oy + y·k); angles are radians, clockwise on screen (Circle's convention).
   - PURE: a function of its arguments and the engine clock T (write-ons from o.t0); the only state is sprite caches.
   - CACHED: static shapes are baked once (engine spriteIn) and blitted with a transform; a radius is bucketed (8 steps
     per octave) and the sprite is scaled to the exact size, so sizes that animate reuse sprites. No shadowBlur.
   - Ink: navy fine-liner with a deterministic hand wobble on details (never on the measured outline), red pen for marks,
     highlighter yellow (multiply). Text through NB.write / direct fillText in the notebook faces (π from pifont.js).
   ========================================================================== */
(function () {
  const PALN = NB.PAL, INK = PALN.ink, RED = PALN.redpen, HI = PALN.hi, PENCIL = PALN.pencil, PAGE = PALN.page;
  const HAND = NB.FONT.hand, UIF = NB.FONT.ui;
  const PI = Math.PI;
  const c01 = x => (x < 0 ? 0 : x > 1 ? 1 : x);
  const clampN = (x, a, b) => Math.max(a, Math.min(b, x));
  const rm = () => !!(window.Quality && Quality.reducedMotion);
  const lowQ = () => !!(window.Quality && Quality.tier === 'low');
  const fontsIn = () => !document.fonts || document.fonts.status === 'loaded';
  const has = (o, k) => o && o[k] !== undefined && o[k] !== null;
  function seedOf(s) { let h = 2166136261; for (const ch of String(s)) h = Math.imul(h ^ ch.charCodeAt(0), 16777619); return h >>> 0; }
  const rnd = (seed, i) => h2(seed | 0, i | 0);          // deterministic 0..1 (engine h2)
  /* progress of a mark over [t0, t0 + dur] (t0 absent = already drawn); reduced motion: a short fade */
  function prog(t0, dur) {
    if (t0 === undefined || t0 === null) return { p: 1, a: 1 };
    if (rm()) return { p: T >= t0 ? 1 : 0, a: c01((T - t0) / 0.2) };
    return { p: E.outCubic(c01((T - t0) / Math.max(1e-3, dur))), a: T >= t0 ? 1 : 0 };
  }
  function multiply(fn) {
    g.save();
    if (lowQ()) g.globalAlpha *= 0.6; else g.globalCompositeOperation = 'multiply';
    try { fn(); } finally { g.restore(); }
  }

  /* ---------------- sprite cache (engine spriteIn) + stats ---------------- */
  const STATS = {};
  const slots = new Map();
  const stat = kind => STATS[kind] || (STATS[kind] = { hits: 0, bakes: 0, direct: 0 });
  /* draw() in local units, box bx..bh; baked once per key, blitted after. Returns true when blitted. */
  function cached(kind, key, bx, by, bw, bh, draw, needFonts) {
    const st = stat(kind);
    if (FX.sprites && !_spriteBaking && (!needFonts || fontsIn()) && _ctm()) {
      let sl = slots.get(key);
      if (!sl) { if (slots.size > 4000) slots.clear(); slots.set(key, sl = {}); }
      const b0 = OFF.stats.bakes;
      if (spriteIn(sl, bx, by, bw, bh, draw, 3)) { if (OFF.stats.bakes > b0) st.bakes++; else st.hits++; return true; }
    }
    st.direct++;
    g.save(); try { draw(); } finally { g.restore(); }
    return false;
  }
  /* radius buckets: 8 per octave; a sprite baked at the bucket radius is drawn scaled to the exact radius */
  const bucket = R => Math.pow(2, Math.round(Math.log2(Math.max(2, R)) * 8) / 8);
  /* line weights: a fine-liner keeps its width whatever the size (gently thinner on tiny things) */
  const LW = R => clampN(R * 0.03, 1.6, 4);

  /* ---------------- hand-drawn lines ---------------- */
  /* a polyline resampled and bowed sideways by a smooth seeded wobble (amp px); the END POINTS stay exact */
  function wob(pts, seed, amp = 0.8, step = 8) {
    const rp = resample(pts, step), n = rp.length;
    if (n < 3 || !amp) return rp;
    const L = [0];
    for (let i = 1; i < n; i++) L.push(L[i - 1] + Math.hypot(rp[i][0] - rp[i - 1][0], rp[i][1] - rp[i - 1][1]));
    const tot = L[n - 1] || 1, p1 = rnd(seed, 1) * TAU, p2 = rnd(seed, 2) * TAU, f1 = 0.012 + 0.01 * rnd(seed, 3), f2 = 0.045 + 0.02 * rnd(seed, 4);
    return rp.map((p, i) => {
      const a = rp[Math.max(0, i - 1)], b = rp[Math.min(n - 1, i + 1)], dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy) || 1;
      const env = Math.sin(PI * L[i] / tot), off = amp * env * (0.7 * Math.sin(L[i] * f1 + p1) + 0.3 * Math.sin(L[i] * f2 + p2));
      return [p[0] - dy / d * off, p[1] + dx / d * off];
    });
  }
  function strokePts(pts, lw, color, alpha = 1) {
    if (pts.length < 2) return;
    g.save(); g.lineCap = 'round'; g.lineJoin = 'round'; g.strokeStyle = color; g.lineWidth = lw; g.globalAlpha *= alpha;
    g.beginPath(); smoothPath(pts); g.stroke(); g.restore();
  }
  /* a straight detail line with a hand bow (inside sprites: plain canvas strokes) */
  function hline(x0, y0, x1, y1, lw, color, seed, amp = 0.6, alpha = 1) { strokePts(wob([[x0, y0], [x1, y1]], seed, amp, 6), lw, color, alpha); }
  /* the measured outline of a disc: an exact circle whose ink lies inside R, + the pen's restart overlap (inside) */
  function outline(R, lw, color = INK, seed = 1) {
    g.save(); g.strokeStyle = color; g.lineWidth = lw; g.beginPath(); g.arc(0, 0, R - lw / 2, 0, TAU); g.stroke();
    const a0 = rnd(seed, 7) * TAU, pts = [];
    for (let i = 0; i <= 12; i++) { const a = a0 + i / 12 * 0.5, rr = R - lw * (0.75 + 1.1 * i / 12); pts.push([Math.cos(a) * rr, Math.sin(a) * rr]); }
    g.restore();
    strokePts(pts, lw * 0.55, color, 0.7);
  }
  const circlePath = (r, x = 0, y = 0) => { g.beginPath(); g.arc(x, y, r, 0, TAU); };
  function ring(r, lw, color, alpha = 1) { g.save(); g.globalAlpha *= alpha; g.strokeStyle = color; g.lineWidth = lw; circlePath(r); g.stroke(); g.restore(); }
  function dot(x, y, r, color) { g.fillStyle = color; circlePath(r, x, y); g.fill(); }
  /* a seeded blob (a paint spot, a stamp): n lobes around (x, y), radii rx, ry */
  function blobPath(x, y, rx, ry, seed, n = 9, wig = 0.18) {
    const pts = [];
    for (let i = 0; i < n; i++) { const a = i / n * TAU, k = 1 + (rnd(seed, i) - 0.5) * 2 * wig; pts.push([x + Math.cos(a) * rx * k, y + Math.sin(a) * ry * k]); }
    g.beginPath();
    const m = (p, q) => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2], s = m(pts[n - 1], pts[0]);
    g.moveTo(s[0], s[1]);
    for (let i = 0; i < n; i++) { const p = pts[i], q = m(p, pts[(i + 1) % n]); g.quadraticCurveTo(p[0], p[1], q[0], q[1]); }
    g.closePath();
  }

  /* ---------------- the bike wheel ---------------- */
  /* local frame: hub at (0, 0), the paint spot at local (0, R) (= "down" at angle 0; Circle.mark: rotate by +angle) */
  function drawWheel(R, paint) {
    const lw = LW(R), tw = Math.max(lw * 2.4, R * 0.085), rimIn = R - tw - Math.max(lw * 1.2, R * 0.04);
    g.save(); circlePath(R); g.clip();
    g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(0, 0, R - tw, 0, TAU, true); g.fillStyle = 'rgba(29,43,79,0.22)'; g.fill();   // the tyre
    if (R > 40) {                                                       // tread
      const n = R > 120 ? 64 : 40;
      for (let i = 0; i < n; i++) {
        const a = i / n * TAU, b = a + (i % 2 ? 0.03 : -0.03), r0 = R - tw * 0.28, r1 = R - tw * 0.72;
        hline(Math.cos(a) * r0, Math.sin(a) * r0, Math.cos(b) * r1, Math.sin(b) * r1, lw * 0.45, INK, 300 + i, 0, 0.75);
      }
    }
    outline(R, lw, INK, 11);
    ring(R - tw, lw * 0.8, INK);                                       // tyre / rim line
    ring(rimIn, lw * 0.7, INK);                                        // the rim's inner edge
    // spokes, tangentially laced from the hub flange to the rim
    const ns = R >= 70 ? 32 : R >= 35 ? 16 : 8, hf = R * 0.1;
    for (let j = 0; j < ns; j++) {
      const a = j / ns * TAU, dir = j % 2 ? 1 : -1, b = a + dir * (ns >= 32 ? 0.42 : 0.25);
      hline(Math.cos(a) * hf, Math.sin(a) * hf, Math.cos(b) * rimIn, Math.sin(b) * rimIn, Math.max(1, lw * 0.42), INK, 100 + j, 0.5, 0.95);
    }
    ring(hf, lw * 0.6, INK, 0.9);                                      // flange
    g.fillStyle = PAGE; circlePath(R * 0.075); g.fill(); ring(R * 0.075 - lw * 0.35, lw * 0.7, INK);
    dot(0, 0, Math.max(1.6, R * 0.03), INK);                           // axle
    // the valve stem (shows the turn even without paint)
    const va = -PI / 2 - 0.55, vr0 = R - tw, vr1 = R - tw - R * 0.07;
    hline(Math.cos(va) * vr0, Math.sin(va) * vr0, Math.cos(va) * vr1, Math.sin(va) * vr1, lw * 1.1, INK, 5, 0);
    if (paint !== false) {                                             // one wet-paint spot on the tyre, + a drip
      const pr = Math.max(5.5, R * 0.11);
      g.fillStyle = RED; blobPath(0, R - pr * 0.72, pr * 1.05, pr * 0.95, 77, 9, 0.16); g.fill();
      g.beginPath(); g.moveTo(-pr * 0.05, R - pr * 1.2); g.quadraticCurveTo(pr * 0.35, R - pr * 1.9, pr * 0.18, R - pr * 2.45);
      g.lineWidth = Math.max(2, pr * 0.42); g.lineCap = 'round'; g.strokeStyle = RED; g.stroke();
      dot(pr * 0.18, R - pr * 2.5, Math.max(1.6, pr * 0.32), RED);
      g.globalAlpha = 0.55; g.fillStyle = '#fff'; g.beginPath(); g.ellipse(-pr * 0.35, R - pr * 0.95, pr * 0.24, pr * 0.14, -0.5, 0, TAU); g.fill();
    }
    g.restore();
  }

  /* ---------------- the pizza ---------------- */
  const CHEESE = '#fbe9a8', CRUST = '#efd09a', PEP = '#e2685a', PEPD = '#a8352b', HERB = '#36573f';
  const CRUST_IN = 0.86;                                              // the crust's inner edge, as a fraction of R
  /* the toppings, in a unit pizza (radius 1), from a seed: pepperoni and herb flecks that never overlap */
  const topsCache = new Map();
  function tops(seed = 7) {
    let T_ = topsCache.get(seed);
    if (T_) return T_;
    const r = rng(seed * 7919 + 3), out = [];
    for (let tries = 0; out.length < 9 && tries < 600; tries++) {
      const a = r() * TAU, rho = 0.2 + r() * 0.5, p = { kind: 'pep', x: Math.cos(a) * rho, y: Math.sin(a) * rho, r: 0.088 };
      if (out.every(q => Math.hypot(q.x - p.x, q.y - p.y) > q.r + p.r + 0.05)) out.push(p);
    }
    const peps = out.length;
    for (let tries = 0; out.length < peps + 16 && tries < 800; tries++) {
      const a = r() * TAU, rho = 0.1 + r() * 0.66, p = { kind: 'herb', x: Math.cos(a) * rho, y: Math.sin(a) * rho, r: 0.035, ang: r() * PI };
      if (out.every(q => Math.hypot(q.x - p.x, q.y - p.y) > q.r + p.r + 0.02)) out.push(p);
    }
    topsCache.set(seed, T_ = out);
    return T_;
  }
  /* one topping, drawn in a frame whose unit is the pizza radius (u = 1 / that radius in px: line widths) */
  function topping(p, u) {
    if (p.kind === 'pep') {
      g.fillStyle = PEP; circlePath(p.r, p.x, p.y); g.fill();
      g.lineWidth = 1.6 * u; g.strokeStyle = PEPD; circlePath(p.r - 0.8 * u, p.x, p.y); g.stroke();
      g.fillStyle = PEPD;
      for (let i = 0; i < 3; i++) { const a = i * 2.1 + p.x * 9, d = p.r * 0.45; circlePath(Math.max(0.9 * u, p.r * 0.13), p.x + Math.cos(a) * d, p.y + Math.sin(a) * d); g.fill(); }
    } else {
      const c = Math.cos(p.ang) * p.r, s = Math.sin(p.ang) * p.r;
      g.lineCap = 'round'; g.lineWidth = Math.max(1.6 * u, p.r * 0.45); g.strokeStyle = HERB;
      g.beginPath(); g.moveTo(p.x - c, p.y - s); g.quadraticCurveTo(p.x + s * 0.4, p.y - c * 0.4, p.x + c, p.y + s); g.stroke();
    }
  }
  /* the whole pizza in a local frame (radius R): cheese, crust band, red-pen crust arc, toppings */
  function drawPizza(R, seed) {
    const lw = LW(R);
    g.save(); circlePath(R); g.clip();
    g.fillStyle = CHEESE; circlePath(R); g.fill();
    g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(0, 0, R * CRUST_IN, 0, TAU, true); g.fillStyle = CRUST; g.fill();
    ring(R * CRUST_IN, lw * 0.6, INK, 0.75);
    g.save(); g.scale(R, R); for (const p of tops(seed)) topping(p, 1 / R); g.restore();
    g.restore();
    g.save(); circlePath(R); g.clip(); outline(R, lw * 1.6, RED, seed); g.restore();
  }
  /* ONE sector sprite in its local frame (apex at 0, crust up), radius R, n sectors: Circle.shape(R, n) */
  const shapeCache = new Map();
  function shapeOf(R, n) { const k = R + '|' + n; let s = shapeCache.get(k); if (!s) { if (shapeCache.size > 400) shapeCache.clear(); shapeCache.set(k, s = Circle.shape(R, n, { arc: Math.max(8, Math.min(48, Math.round(96 / n * 4))) })); } return s; }
  function sectorPath(sh) { g.beginPath(); g.moveTo(0, 0); for (const p of sh.crust) g.lineTo(p[0], p[1]); g.closePath(); }
  function drawSector(R, n) {
    const sh = shapeOf(R, n), h = sh.half, lw = LW(R);
    g.save(); sectorPath(sh); g.fillStyle = CHEESE; g.fill(); g.clip();
    g.beginPath(); g.arc(0, 0, R, -PI / 2 - h, -PI / 2 + h); g.arc(0, 0, R * CRUST_IN, -PI / 2 + h, -PI / 2 - h, true); g.closePath(); g.fillStyle = CRUST; g.fill();
    g.beginPath(); g.arc(0, 0, R * CRUST_IN, -PI / 2 - h, -PI / 2 + h); g.lineWidth = lw * 0.6; g.strokeStyle = INK; g.globalAlpha = 0.75; g.stroke(); g.globalAlpha = 1;
    g.beginPath(); g.arc(0, 0, R, -PI / 2 - h - 0.01, -PI / 2 + h + 0.01); g.lineWidth = lw * 3.2; g.strokeStyle = RED; g.stroke();   // crust: lw·1.6 visible
    g.beginPath(); g.moveTo(sh.left[0], sh.left[1]); g.lineTo(0, 0); g.lineTo(sh.right[0], sh.right[1]);
    // the cut edges (half inside each slice: two neighbours make one line); thinner as the slices get thinner, and
    // fading toward the apex from 16 slices on, so the centre of a finely cut pizza doesn't turn into an ink blot
    g.lineJoin = 'round'; g.lineWidth = lw * 1.3 * clampN(12 / n, 0.4, 1);
    if (n >= 16) { const gr = g.createLinearGradient(0, 0, 0, -R * 0.45); gr.addColorStop(0, 'rgba(29,43,79,0.05)'); gr.addColorStop(1, 'rgba(29,43,79,0.9)'); g.strokeStyle = gr; }
    else g.strokeStyle = INK;
    g.stroke();
    g.restore();
  }
  /* which toppings touch which sector (u = 0, the circle's frame), in each sector's LOCAL unit frame */
  const secTopsCache = new Map();
  function secTops(n, seed) {
    const key = n + '|' + seed;
    let L = secTopsCache.get(key);
    if (L) return L;
    const S = Circle.sectors(1, n), all = tops(seed);
    L = S.map(s => {
      const rot = s.mid + PI / 2, c = Math.cos(-rot), sn = Math.sin(-rot), list = [];
      for (const p of all) {
        const rho = Math.hypot(p.x, p.y), a = Math.atan2(p.y, p.x), half = rho > p.r ? Math.asin(Math.min(1, p.r / rho)) : PI;
        let d = a - s.mid; d = ((d + PI) % TAU + TAU) % TAU - PI;
        if (Math.abs(d) <= s.half + half + 1e-9) list.push(Object.assign({}, p, { x: c * p.x - sn * p.y, y: sn * p.x + c * p.y, ang: (p.ang || 0) - rot }));
      }
      return list;
    });
    if (secTopsCache.size > 64) secTopsCache.clear();
    secTopsCache.set(key, L);
    return L;
  }
  const unitPaths = new Map();
  function unitPath(n) {
    let p = unitPaths.get(n);
    if (!p) { const sh = Circle.shape(1, n, { arc: 48 }); p = new Path2D(); p.moveTo(0, 0); for (const q of sh.crust) p.lineTo(q[0], q[1]); p.closePath(); unitPaths.set(n, p); }
    return p;
  }

  /* ---------------- the shelf discs and job shapes: draw functions in a local frame of radius R ---------------- */
  const DISC = {};
  DISC.wheel = R => drawWheel(R, false);
  DISC.bike = DISC.wheel;
  DISC.pizza = R => drawPizza(R, 7);
  DISC.quarter = R => {                                                // a coin: reeded edge, rings, "lettering" ticks
    const lw = LW(R);
    g.fillStyle = '#e4e6ea'; circlePath(R); g.fill();
    const nr = Math.max(40, Math.min(120, Math.round(R * 1.2)));
    g.strokeStyle = PENCIL; g.lineWidth = Math.max(0.8, lw * 0.4); g.beginPath();
    for (let i = 0; i < nr; i++) { const a = i / nr * TAU; g.moveTo(Math.cos(a) * R * 0.995, Math.sin(a) * R * 0.995); g.lineTo(Math.cos(a) * R * 0.92, Math.sin(a) * R * 0.92); }
    g.stroke();
    ring(R * 0.9, lw * 0.7, INK);
    ring(R * 0.62, lw * 0.5, INK, 0.8);
    // the inscription as small ink strokes along two arcs (top long, bottom short: the turn shows)
    const word = (a0, a1, rr, seed) => {
      const n = Math.max(6, Math.round((a1 - a0) * rr / Math.max(3.5, R * 0.07)));
      for (let i = 0; i < n; i++) {
        if (rnd(seed, i) < 0.16) continue;
        const a = a0 + (i + 0.5) / n * (a1 - a0), h = R * (0.07 + 0.03 * rnd(seed, i + 50));
        hline(Math.cos(a) * rr, Math.sin(a) * rr, Math.cos(a) * (rr - h), Math.sin(a) * (rr - h), Math.max(1, lw * 0.5), INK, seed + i, 0);
      }
    };
    word(-PI + 0.35, -0.35, R * 0.85, 21);
    word(0.75, PI - 0.75, R * 0.85, 22);
    // the centre: an engraved rosette of short arcs
    g.strokeStyle = INK; g.lineWidth = Math.max(1, lw * 0.5);
    for (let i = 0; i < 8; i++) { const a = i / 8 * TAU; g.beginPath(); g.ellipse(Math.cos(a) * R * 0.25, Math.sin(a) * R * 0.25, R * 0.2, R * 0.08, a, 0, TAU); g.stroke(); }
    dot(0, 0, R * 0.06, INK);
    dot(Math.cos(0.3) * R * 0.48, Math.sin(0.3) * R * 0.48, R * 0.045, INK);
    outline(R, lw, INK, 31);
  };
  DISC.rim = R => {                                                    // a basketball rim from above: the ring, hooks, net
    const lw = LW(R), band = Math.max(lw * 2.4, R * 0.08), nb = R * 0.52, nh = 12;
    g.strokeStyle = PENCIL; g.lineWidth = Math.max(1, lw * 0.45);
    for (let i = 0; i < nh; i++) {                                     // the net: a diamond lattice down to its lower ring
      const a = i / nh * TAU, b = (i + 0.5) / nh * TAU, c = (i - 0.5) / nh * TAU, r0 = R - band, rm_ = (r0 + nb) / 2;
      g.beginPath(); g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0); g.lineTo(Math.cos(b) * rm_, Math.sin(b) * rm_); g.lineTo(Math.cos(a) * nb, Math.sin(a) * nb);
      g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0); g.lineTo(Math.cos(c) * rm_, Math.sin(c) * rm_); g.lineTo(Math.cos(a) * nb, Math.sin(a) * nb); g.stroke();
    }
    ring(nb, lw * 0.6, INK, 0.8);
    g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(0, 0, R - band, 0, TAU, true); g.fillStyle = '#e8774a'; g.fill();
    ring(R - band, lw * 0.7, INK);
    for (let i = 0; i < nh; i++) { const a = i / nh * TAU, r0 = R - band * 0.5; g.fillStyle = PAGE; circlePath(Math.max(1.4, band * 0.18), Math.cos(a) * r0, Math.sin(a) * r0); g.fill(); }
    g.fillStyle = INK; g.save(); g.rotate(-PI / 2); g.fillRect(R - band * 1.6, -R * 0.09, band * 1.6, R * 0.18); g.restore();   // the bracket
    outline(R, lw, INK, 41);
  };
  DISC.spaceship = R => {                                              // Spaceship Earth: a sphere of triangle panels
    const lw = LW(R), sp = R / 4.2;
    g.fillStyle = '#e9edf3'; circlePath(R); g.fill();
    // every other triangle shaded (a geodesic look), then the three line families
    const h = sp * Math.sqrt(3) / 2, rows = Math.ceil(R / h) + 1, cols = Math.ceil(R / sp) + 2;
    g.fillStyle = 'rgba(29,43,79,0.12)';
    for (let j = -rows; j <= rows; j++) for (let i = -cols; i <= cols; i++) {
      if ((i + j) % 3) continue;
      const x = i * sp + (j % 2 ? sp / 2 : 0), y = j * h;
      g.beginPath(); g.moveTo(x, y); g.lineTo(x + sp, y); g.lineTo(x + sp / 2, y + h); g.closePath(); g.fill();
    }
    g.strokeStyle = INK; g.lineWidth = Math.max(0.9, lw * 0.4); g.beginPath();
    for (const a of [0, PI / 3, 2 * PI / 3]) {
      const c = Math.cos(a), s = Math.sin(a), d = a === 0 ? h : h;
      for (let k = -rows; k <= rows; k++) { const o = k * d; g.moveTo(-s * o - c * R * 1.2, c * o - s * R * 1.2); g.lineTo(-s * o + c * R * 1.2, c * o + s * R * 1.2); }
    }
    g.stroke();
    outline(R, lw, INK, 51);
  };
  DISC.cake = R => {                                                   // a round cake from above: frosting, candles, writing
    const lw = LW(R);
    g.fillStyle = '#ffe3ea'; circlePath(R); g.fill();
    const nbump = 22, rb = R * 0.86;
    g.strokeStyle = INK; g.lineWidth = lw * 0.6;
    for (let i = 0; i < nbump; i++) { const a = i / nbump * TAU; g.beginPath(); g.arc(Math.cos(a) * rb, Math.sin(a) * rb, R * 0.075, a - PI / 2, a + PI / 2); g.stroke(); }
    ring(R * 0.76, lw * 0.5, INK, 0.7);
    const pts = []; for (let i = 0; i <= 24; i++) { const x = -R * 0.42 + i / 24 * R * 0.84; pts.push([x, R * 0.08 + Math.sin(i * 1.3) * R * 0.06]); }
    strokePts(pts, lw * 0.8, RED);
    for (let i = 0; i < 6; i++) { const a = -PI / 2 + (i - 2.5) * 0.42, rr = R * 0.5; g.fillStyle = '#fff'; circlePath(R * 0.05, Math.cos(a) * rr, Math.sin(a) * rr); g.fill(); ring2(Math.cos(a) * rr, Math.sin(a) * rr, R * 0.05, lw * 0.5, INK); dot(Math.cos(a) * rr, Math.sin(a) * rr, R * 0.02, RED); }
    outline(R, lw, INK, 61);
  };
  function ring2(x, y, r, lw, color) { g.strokeStyle = color; g.lineWidth = lw; circlePath(r, x, y); g.stroke(); }
  DISC.table = R => {                                                  // a round wooden table top: grain, bevel, a knot
    const lw = LW(R);
    g.fillStyle = '#efe0c2'; circlePath(R); g.fill();
    const sp = R / 6;
    for (let k = -6; k <= 6; k++) {
      const pts = []; for (let i = 0; i <= 20; i++) { const x = -R + i / 20 * 2 * R; pts.push([x, k * sp + Math.sin(x / R * 3 + k) * sp * 0.18]); }
      strokePts(pts, Math.max(0.9, lw * 0.4), '#a48a63', 0.9);
    }
    g.strokeStyle = '#a48a63'; g.lineWidth = Math.max(1, lw * 0.5); g.beginPath(); g.ellipse(R * 0.3, -R * 0.25, R * 0.1, R * 0.05, 0.1, 0, TAU); g.stroke();
    g.beginPath(); g.ellipse(R * 0.3, -R * 0.25, R * 0.04, R * 0.02, 0.1, 0, TAU); g.stroke();
    ring(R * 0.9, lw * 0.6, INK, 0.8);
    outline(R, lw, INK, 71);
  };
  DISC.garden = R => {                                                 // a round flower bed: edging stones, flowers
    const lw = LW(R);
    g.fillStyle = '#e6d6bb'; circlePath(R); g.fill();
    const ns = 20, rs = R * 0.92;
    for (let i = 0; i < ns; i++) { const a = (i + 0.5) / ns * TAU; g.fillStyle = '#d9dbe0'; g.beginPath(); g.ellipse(Math.cos(a) * rs, Math.sin(a) * rs, R * 0.14, R * 0.075, a + PI / 2, 0, TAU); g.fill(); g.lineWidth = lw * 0.5; g.strokeStyle = INK; g.stroke(); }
    const r = rng(91);
    for (let i = 0; i < 7; i++) {
      const a = i / 7 * TAU + r() * 0.5, rho = i === 0 ? 0 : R * (0.35 + r() * 0.3), x = i === 0 ? 0 : Math.cos(a) * rho, y = i === 0 ? 0 : Math.sin(a) * rho, fr = R * 0.07;
      for (let p = 0; p < 5; p++) { const b = p / 5 * TAU + a; g.fillStyle = '#fff'; circlePath(fr, x + Math.cos(b) * fr * 1.1, y + Math.sin(b) * fr * 1.1); g.fill(); ring2(x + Math.cos(b) * fr * 1.1, y + Math.sin(b) * fr * 1.1, fr, lw * 0.45, INK); }
      dot(x, y, fr * 0.7, RED);
    }
    outline(R, lw, INK, 81);
  };
  DISC.rug = R => {                                                    // a braided rug: rings of braid, the end tucked in
    const lw = LW(R), fills = ['#f4d9de', '#fcfcf8', '#f7efc4', '#dfe8f3'];
    for (let k = 0; k < 7; k++) { const rr = R * (1 - k / 7.6); g.fillStyle = fills[k % 4]; circlePath(rr); g.fill(); ring(rr, lw * 0.45, INK, 0.75); }
    g.strokeStyle = INK; g.lineWidth = Math.max(0.8, lw * 0.35);
    for (let k = 0; k < 6; k++) {
      const r0 = R * (1 - k / 7.6) - R * 0.02, r1 = R * (1 - (k + 1) / 7.6) + R * 0.02, n = Math.round(10 + 40 * (1 - k / 7));
      g.beginPath();
      for (let i = 0; i < n; i++) { const a = i / n * TAU, b = a + 0.5 / n * TAU * (k % 2 ? 1 : -1); g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0); g.lineTo(Math.cos(b) * r1, Math.sin(b) * r1); }
      g.stroke();
    }
    strokePts([[0, 0], [R * 0.06, -R * 0.03], [R * 0.12, R * 0.02]], lw * 0.8, INK);
    outline(R, lw, INK, 91);
  };
  DISC.mound = R => {                                                  // a pitcher's mound: dirt, the rubber, cleat marks
    const lw = LW(R);
    g.fillStyle = '#e7cfa8'; circlePath(R); g.fill();
    g.fillStyle = 'rgba(115,90,60,0.5)';
    for (let i = 0; i < 70; i++) { const a = rnd(101, i) * TAU, rho = Math.sqrt(rnd(102, i)) * R * 0.95; circlePath(Math.max(0.8, R * 0.012), Math.cos(a) * rho, Math.sin(a) * rho); g.fill(); }
    ring(R * 0.55, lw * 0.4, '#a48a63', 0.7);
    g.fillStyle = '#fff'; g.fillRect(-R * 0.2, -R * 0.13, R * 0.4, R * 0.08); g.lineWidth = lw * 0.7; g.strokeStyle = INK; g.strokeRect(-R * 0.2, -R * 0.13, R * 0.4, R * 0.08);
    for (let i = 0; i < 4; i++) hline(-R * 0.08 + i * R * 0.05, R * 0.05, -R * 0.1 + i * R * 0.05, R * 0.14, lw * 0.5, '#8b6f47', 110 + i, 0);
    outline(R, lw, INK, 111);
  };
  DISC.puck = R => {                                                   // a hockey puck from above: black, a logo ring
    const lw = LW(R);
    g.fillStyle = '#20263a'; circlePath(R); g.fill();
    ring(R * 0.86, Math.max(1, lw * 0.5), '#6f7890');
    ring(R * 0.5, Math.max(1, lw * 0.6), '#9aa3b8');
    g.strokeStyle = '#9aa3b8'; g.lineWidth = Math.max(1, lw * 0.5);
    for (let i = 0; i < 9; i++) { const a = -PI * 0.85 + i * 0.2; g.beginPath(); g.moveTo(Math.cos(a) * R * 0.66, Math.sin(a) * R * 0.66); g.lineTo(Math.cos(a) * R * 0.76, Math.sin(a) * R * 0.76); g.stroke(); }
    // the logo: two crossed hockey sticks
    g.strokeStyle = '#c3c9d6'; g.lineWidth = Math.max(1.4, R * 0.05); g.lineCap = 'round';
    for (const sx of [-1, 1]) { g.beginPath(); g.moveTo(sx * R * 0.24, -R * 0.26); g.lineTo(-sx * R * 0.14, R * 0.2); g.lineTo(-sx * R * 0.26, R * 0.22); g.stroke(); }
    g.save(); g.globalAlpha = 0.35; g.strokeStyle = '#fff'; g.lineWidth = lw; g.beginPath(); g.arc(0, 0, R * 0.92, -2.6, -1.9); g.stroke(); g.restore();
    outline(R, lw, INK, 121);
  };
  DISC.court = R => {                                                  // a centre circle on a wood floor: planks, the line
    const lw = LW(R), sp = R / 5;
    g.fillStyle = '#f0dfbf'; circlePath(R); g.fill();
    g.strokeStyle = '#b89b6a'; g.lineWidth = Math.max(0.8, lw * 0.4); g.beginPath();
    for (let k = -5; k <= 5; k++) { g.moveTo(-R, k * sp); g.lineTo(R, k * sp); const jx = ((k * 37) % 5) / 5 * R - R * 0.5; g.moveTo(jx, k * sp); g.lineTo(jx, (k + 1) * sp); }
    g.stroke();
    g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(0, 0, R - Math.max(lw * 2, R * 0.07), 0, TAU, true); g.fillStyle = RED; g.fill();
    ring(R * 0.34, Math.max(lw * 1.6, R * 0.05), RED);
    g.fillStyle = RED; g.fillRect(-Math.max(lw, R * 0.025), -R, Math.max(2 * lw, R * 0.05), 2 * R);    // the half-court line
    outline(R, lw, INK, 131);
  };
  DISC.disc = R => {                                                   // a CD: silver, the hole, the clamp ring, a sheen
    const lw = LW(R);
    g.fillStyle = '#e9ebf0'; circlePath(R); g.fill();
    for (let k = 0; k < 6; k++) ring(R * (0.42 + k * 0.09), Math.max(0.6, lw * 0.25), PENCIL, 0.5);
    multiply(() => {
      g.lineWidth = R * 0.16; g.lineCap = 'butt';
      g.strokeStyle = 'rgba(246,234,74,0.7)'; g.beginPath(); g.arc(0, 0, R * 0.7, -2.4, -1.5); g.stroke();
      g.strokeStyle = 'rgba(140,199,217,0.6)'; g.beginPath(); g.arc(0, 0, R * 0.7, -1.5, -0.8); g.stroke();
      g.strokeStyle = 'rgba(242,163,156,0.6)'; g.beginPath(); g.arc(0, 0, R * 0.7, 0.8, 1.6); g.stroke();
    });
    g.fillStyle = '#f6f7f9'; circlePath(R * 0.33); g.fill(); ring(R * 0.33, lw * 0.5, INK, 0.8); ring(R * 0.24, lw * 0.4, PENCIL);
    g.fillStyle = PAGE; circlePath(R * 0.125); g.fill(); ring(R * 0.125, lw * 0.7, INK);
    outline(R, lw, INK, 141);
  };
  DISC.pool = R => {                                                   // a round pool: coping, water ripples, a ladder
    const lw = LW(R), cw = Math.max(lw * 2.4, R * 0.09);
    g.fillStyle = '#d6eaf6'; circlePath(R); g.fill();
    g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(0, 0, R - cw, 0, TAU, true); g.fillStyle = '#f4f2ea'; g.fill();
    ring(R - cw, lw * 0.6, INK);
    const r = rng(151);
    for (let i = 0; i < 9; i++) {
      const a = r() * TAU, rho = r() * R * 0.62, x = Math.cos(a) * rho, y = Math.sin(a) * rho, L = R * (0.12 + r() * 0.1), pts = [];
      for (let j = 0; j <= 8; j++) pts.push([x - L + j / 8 * 2 * L, y + Math.sin(j / 8 * TAU) * R * 0.02]);
      strokePts(pts, Math.max(1, lw * 0.5), '#5f8fb8', 0.9);
    }
    const lx = R - cw * 0.5;
    for (const dy of [-R * 0.09, R * 0.09]) hline(lx - R * 0.2, dy, lx + cw * 0.3, dy, lw * 0.8, INK, 160, 0);
    for (let k = 1; k <= 2; k++) hline(lx - R * 0.07 * k - R * 0.02, -R * 0.09, lx - R * 0.07 * k - R * 0.02, R * 0.09, lw * 0.6, INK, 161, 0);
    outline(R, lw, INK, 171);
  };
  DISC.clock = R => {                                                  // a wall clock: ticks, 12 3 6 9, 10:10
    const lw = LW(R);
    g.fillStyle = '#fff'; circlePath(R); g.fill();
    ring(R * 0.93, lw * 0.5, INK, 0.8);
    g.strokeStyle = INK;
    for (let i = 0; i < 60; i++) {
      const a = i / 60 * TAU, big = i % 5 === 0, r0 = R * 0.9, r1 = R * (big ? 0.78 : 0.85);
      g.lineWidth = big ? lw * 0.9 : Math.max(0.7, lw * 0.35); g.beginPath(); g.moveTo(Math.cos(a) * r0, Math.sin(a) * r0); g.lineTo(Math.cos(a) * r1, Math.sin(a) * r1); g.stroke();
    }
    if (R >= 26) {
      g.fillStyle = INK; g.font = `700 ${Math.round(R * 0.2)}px ${HAND}`; g.textAlign = 'center'; g.textBaseline = 'middle';
      [['12', -PI / 2], ['3', 0], ['6', PI / 2], ['9', PI]].forEach(([s, a]) => g.fillText(s, Math.cos(a) * R * 0.62, Math.sin(a) * R * 0.62 + R * 0.02));
    }
    const hand = (a, L, w, c) => { g.strokeStyle = c; g.lineWidth = w; g.lineCap = 'round'; g.beginPath(); g.moveTo(-Math.cos(a) * L * 0.15, -Math.sin(a) * L * 0.15); g.lineTo(Math.cos(a) * L, Math.sin(a) * L); g.stroke(); };
    hand(-PI / 2 + (10 + 10 / 60) / 12 * TAU, R * 0.45, lw * 1.3, INK);
    hand(-PI / 2 + 10 / 60 * TAU, R * 0.7, lw * 0.9, INK);
    dot(0, 0, Math.max(2, R * 0.05), RED);
    outline(R, lw, INK, 181);
  };
  DISC.trampoline = R => {                                             // a trampoline: frame, springs, the dark mat
    const lw = LW(R), fr = Math.max(lw * 2.4, R * 0.08), mat = R * 0.72;
    g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(0, 0, R - fr, 0, TAU, true); g.fillStyle = '#c9ced8'; g.fill();
    ring(R - fr, lw * 0.6, INK);
    g.fillStyle = '#2b3550'; circlePath(mat); g.fill();
    ring(mat * 0.93, Math.max(1, lw * 0.4), '#8892aa');
    const ns = R >= 60 ? 36 : 18;
    for (let i = 0; i < ns; i++) {
      const a = i / ns * TAU, c = Math.cos(a), s = Math.sin(a), r0 = mat, r1 = R - fr, pts = [];
      for (let j = 0; j <= 6; j++) { const rr = r0 + (r1 - r0) * j / 6, z = (j % 2 ? 1 : -1) * (j && j < 6 ? R * 0.018 : 0); pts.push([c * rr - s * z, s * rr + c * z]); }
      g.strokeStyle = INK; g.lineWidth = Math.max(0.8, lw * 0.4); g.beginPath(); g.moveTo(pts[0][0], pts[0][1]); for (const p of pts) g.lineTo(p[0], p[1]); g.stroke();
    }
    g.strokeStyle = '#e8ebf2'; g.lineWidth = Math.max(1, lw * 0.6);
    g.beginPath(); g.moveTo(-mat * 0.2, -mat * 0.2); g.lineTo(mat * 0.2, mat * 0.2); g.moveTo(mat * 0.2, -mat * 0.2); g.lineTo(-mat * 0.2, mat * 0.2); g.stroke();
    outline(R, lw, INK, 191);
  };
  DISC.dartboard = R => {                                              // a dartboard: 20 segments, double + treble, bull
    const lw = LW(R), play = R * 0.78, seg = TAU / 20, off = -PI / 2 - seg / 2;
    g.fillStyle = '#20263a'; circlePath(R); g.fill();
    for (let i = 0; i < 20; i++) {
      const a0 = off + i * seg, a1 = a0 + seg;
      const band = (r0, r1, c) => { g.beginPath(); g.arc(0, 0, r1, a0, a1); g.arc(0, 0, r0, a1, a0, true); g.closePath(); g.fillStyle = c; g.fill(); };
      band(0, play, i % 2 ? '#f5ecd6' : '#2b2f3c');
      band(play * 0.94, play, i % 2 ? '#3f7d4f' : RED);
      band(play * 0.56, play * 0.62, i % 2 ? '#3f7d4f' : RED);
    }
    g.strokeStyle = '#c9ccd4'; g.lineWidth = Math.max(0.6, lw * 0.3); g.beginPath();
    for (let i = 0; i < 20; i++) { const a = off + i * seg; g.moveTo(Math.cos(a) * R * 0.08, Math.sin(a) * R * 0.08); g.lineTo(Math.cos(a) * play, Math.sin(a) * play); } g.stroke();
    g.fillStyle = '#3f7d4f'; circlePath(R * 0.08); g.fill(); dot(0, 0, R * 0.035, RED);
    g.fillStyle = '#e8e8ec';
    for (let i = 0; i < 20; i++) { const a = off + (i + 0.5) * seg; g.fillRect(Math.cos(a) * R * 0.89 - R * 0.025, Math.sin(a) * R * 0.89 - R * 0.025, R * 0.05, R * 0.05); }
    dot(0, -R * 0.89, R * 0.035, RED);                                 // the "20" mark at the top (the turn shows)
    outline(R, lw, INK, 201);
  };
  DISC.circle = R => { g.fillStyle = '#fff'; circlePath(R); g.fill(); outline(R, LW(R), INK, 211); };   // a plain circle
  const IDS = Object.keys(DISC).filter(k => k !== 'bike');
  const NEED_FONTS = { clock: true };
  /* a static (non-rotating) overlay: the sphere's shading */
  function spaceshipShade(R) {
    g.save(); circlePath(R); g.clip();
    g.fillStyle = 'rgba(29,43,79,0.14)'; g.beginPath(); g.arc(0, 0, R, 0, TAU); g.arc(-R * 0.18, -R * 0.18, R * 0.98, 0, TAU, true); g.fill('evenodd');
    g.restore();
  }

  /* draw a disc kind at (cx, cy), radius R, turned by angle: one sprite per (kind, radius bucket) */
  function disc(kind, cx, cy, R, angle, o = {}) {
    if (!(R > 0.5)) return null;
    const f = DISC[kind] || DISC.circle, Rb = bucket(R), s = R / Rb;
    g.save(); g.translate(cx, cy);
    if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (angle) g.rotate(angle);
    if (s !== 1) g.scale(s, s);
    const key = 'disc|' + kind + '|' + Rb + (o.paint === false ? '|np' : o.paint ? '|p' : '');
    cached('disc', key, -Rb, -Rb, 2 * Rb, 2 * Rb, () => { g.save(); circlePath(Rb); g.clip(); f(Rb); g.restore(); }, NEED_FONTS[kind]);
    g.restore();
    if (kind === 'spaceship' && o.shade !== false) {
      g.save(); g.translate(cx, cy); if (has(o, 'alpha')) g.globalAlpha *= o.alpha; if (s !== 1) g.scale(s, s);
      cached('disc', 'shade|' + Rb, -Rb, -Rb, 2 * Rb, 2 * Rb, () => spaceshipShade(Rb));
      g.restore();
    }
    return { cx, cy, R, x: cx - R, y: cy - R, w: 2 * R, h: 2 * R };
  }

  /* ---------------- public: round things ---------------- */
  const CK = { PAL: { ink: INK, red: RED, hi: HI, pencil: PENCIL, page: PAGE, cheese: CHEESE, crust: CRUST }, ids: IDS, stats: STATS };
  CK.bucket = bucket;
  CK.wob = wob;
  CK.wheel = function (cx, cy, R, angle = 0, o = {}) {
    if (!(R > 0.5)) return null;
    const Rb = bucket(R), s = R / Rb, paint = o.paint !== false;
    g.save(); g.translate(cx, cy);
    if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (angle) g.rotate(angle);
    if (s !== 1) g.scale(s, s);
    cached('wheel', 'wheel|' + Rb + '|' + paint, -Rb, -Rb, 2 * Rb, 2 * Rb, () => drawWheel(Rb, paint));
    g.restore();
    const c = Math.cos(angle), sn = Math.sin(angle);
    return { cx, cy, R, spot: { x: cx - sn * R, y: cy + c * R } };   // local (0, R) turned by +angle
  };
  CK.disc = function (id, cx, cy, R, angle = 0, o = {}) {
    if (id === 'wheel' || id === 'bike') return CK.wheel(cx, cy, R, angle, Object.assign({ paint: false }, o));
    return disc(id, cx, cy, R, angle, o);
  };
  CK.circle = function (cx, cy, R, o = {}) {                         // a plain ink circle (+ centre dot): exact, inside R
    const lw = o.w || 3.5;
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (o.fill) { g.fillStyle = o.fill; circlePath(R, cx, cy); g.fill(); }
    if (o.dash) g.setLineDash(o.dash);
    g.strokeStyle = o.color || INK; g.lineWidth = lw; circlePath(Math.max(0.1, R - lw / 2), cx, cy); g.stroke();
    g.setLineDash([]);
    if (o.center !== false) dot(cx, cy, o.dot || 5, o.color || INK);
    g.restore();
    return { cx, cy, R };
  };

  /* ---------------- the pizza and its slices ---------------- */
  CK.pizza = function (cx, cy, R, o = {}) {
    if (o.cut) return CK.slices(cx, cy, R, 1, o.cut, 0, Object.assign({}, o, { cut: undefined }));
    if (!(R > 0.5)) return null;
    const Rb = bucket(R), s = R / Rb, seed = o.seed || 7;
    g.save(); g.translate(cx, cy);
    if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (o.angle) g.rotate(o.angle);
    if (s !== 1) g.scale(s, s);
    cached('pizza', 'pizza|' + Rb + '|' + seed, -Rb, -Rb, 2 * Rb, 2 * Rb, () => drawPizza(Rb, seed));
    g.restore();
    return { cx, cy, R, x: cx - R, y: cy - R, w: 2 * R, h: 2 * R };
  };
  /* one sector, by a rigid transform tf = {x, y, rot} in model units (Circle.layout's), apex at (ox + x·k, oy + y·k) */
  function sectorAt(ox, oy, k, r, n, tf, i, o, seed) {
    const rk = r * k, Rb = bucket(rk), s = rk / Rb, h = PI / n, sh = Math.sin(h);
    g.save(); g.translate(ox + tf.x * k, oy + tf.y * k); g.rotate(tf.rot);
    g.scale(s, s);
    cached('sector', 'sec|' + n + '|' + Rb, -Rb * sh, -Rb, 2 * Rb * sh, Rb, () => drawSector(Rb, n));
    if (i !== null && i !== undefined && o.toppings !== false) {
      const list = secTops(n, seed)[i];
      if (list && list.length) {
        g.scale(Rb, Rb);
        g.save(); g.clip(unitPath(n));
        for (const p of list) topping(p, 1 / Rb);
        g.restore();
      }
    }
    g.restore();
  }
  function ghostAt(ox, oy, k, r, n, tf) {
    const sh = shapeOf(r * k, n);
    g.save(); g.translate(ox + tf.x * k, oy + tf.y * k); g.rotate(tf.rot);
    g.setLineDash([10, 8]); g.lineWidth = 2.5; g.strokeStyle = PENCIL; sectorPath(sh); g.stroke();
    g.restore();
  }
  function hiAt(ox, oy, k, r, n, tf, color) {
    const sh = shapeOf(r * k, n);
    g.save(); g.translate(ox + tf.x * k, oy + tf.y * k); g.rotate(tf.rot);
    multiply(() => { g.globalAlpha *= 0.55; g.fillStyle = color || HI; sectorPath(sh); g.fill(); });
    g.restore();
  }
  /* bounding box of transformed sectors (exact: the outline points + the crust's extreme) */
  function boxOf(ox, oy, k, r, n, L) {
    const sh = shapeOf(r, n);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const tf of L) {
      const c = Math.cos(tf.rot), s = Math.sin(tf.rot);
      for (const p of sh.outline) {
        const x = c * p[0] - s * p[1] + tf.x, y = s * p[0] + c * p[1] + tf.y;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
    return { x: ox + x0 * k, y: oy + y0 * k, w: (x1 - x0) * k, h: (y1 - y0) * k };
  }
  CK.slices = function (ox, oy, k, r, n, u = 0, o = {}) {
    const L = o.layout || Circle.layout(r, n, u), seed = o.seed || 7;
    const only = o.only ? new Set(o.only) : null, hi = o.hi ? new Set(o.hi) : null;
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    for (const tf of L) {
      if (only && !only.has(tf.i)) { if (o.ghost) ghostAt(ox, oy, k, r, n, tf); continue; }
      sectorAt(ox, oy, k, r, n, tf, tf.i, o, seed);
      if (hi && hi.has(tf.i)) hiAt(ox, oy, k, r, n, tf, o.hiColor);
    }
    g.restore();
    const drawn = only ? L.filter(tf => only.has(tf.i)) : L;
    return {
      layout: L, box: boxOf(ox, oy, k, r, n, drawn),
      at(x, y) {                                                       // the sector under a screen point, or −1
        const sh = shapeOf(r, n), q = [(x - ox) / k, (y - oy) / k];
        for (let j = drawn.length - 1; j >= 0; j--) {
          const p = Circle.unplace(drawn[j], q), a = Math.atan2(p[0], -p[1]);
          if (Math.hypot(p[0], p[1]) <= r && Math.abs(a) <= sh.half) return drawn[j].i;
        }
        return -1;
      },
    };
  };
  CK.sector = function (ox, oy, k, r, n, tf, o = {}) {
    const i = has(o, 'i') ? o.i : has(tf, 'i') ? tf.i : null;
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    sectorAt(ox, oy, k, r, n, tf, i, o, o.seed || 7);
    if (o.hi) hiAt(ox, oy, k, r, n, tf, o.hiColor);
    g.restore();
    return { apex: { x: ox + tf.x * k, y: oy + tf.y * k }, box: boxOf(ox, oy, k, r, n, [tf]) };
  };

  /* ---------------- the floor strip ---------------- */
  CK.strip = function (x0, y, w, o = {}) {
    const h = o.h || 64, torn = o.torn || 'right', seed = o.seed || 5;
    const jag = (x, sgn) => { const pts = [], n = Math.max(4, Math.round(h / 9)); for (let i = 0; i <= n; i++) pts.push([x + sgn * (i % 2 ? 7 + rnd(seed, i) * 6 : rnd(seed, i + 40) * 3), y + i / n * h]); return pts; };
    const draw = () => {
      const R_ = torn === 'right' || torn === 'both' ? jag(w, 1) : [[w, y], [w, y + h]];
      const L_ = torn === 'left' || torn === 'both' ? jag(0, -1).reverse() : [[0, y + h], [0, y]];
      const pts = [[0, y], ...R_.map(p => [p[0], p[1]]), ...L_.map(p => [p[0], p[1]])];
      const path = dx => { g.beginPath(); g.moveTo(pts[0][0] + dx, pts[0][1]); for (const p of pts) g.lineTo(p[0] + dx, p[1]); g.closePath(); };
      g.fillStyle = 'rgba(29,43,79,0.08)'; g.save(); g.translate(3, 6); path(0); g.fill(); g.restore();
      path(0); g.fillStyle = PALN.receipt || '#fffefa'; g.fill(); g.lineWidth = 1.5; g.strokeStyle = 'rgba(29,43,79,0.22)'; g.stroke();
      g.save(); g.setLineDash([2, 10]); g.lineWidth = 1.5; g.strokeStyle = 'rgba(115,121,133,0.35)'; g.beginPath(); g.moveTo(10, y + h - 9); g.lineTo(w - 14, y + h - 9); g.stroke(); g.restore();
      g.lineWidth = 2.5; g.strokeStyle = INK; g.globalAlpha = 0.8; g.beginPath(); g.moveTo(0, y + 1.25); g.lineTo(w - (torn === 'right' || torn === 'both' ? 6 : 0), y + 1.25); g.stroke();
    };
    g.save(); g.translate(x0, 0);
    if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    cached('strip', 'strip|' + Math.round(w) + '|' + h + '|' + torn + '|' + seed + '|' + y, -18, y - 4, w + 50, h + 16, draw);
    g.restore();
    return { x0, x1: x0 + w, y, h };
  };
  CK.stamp = function (x, y, o = {}) {
    const r = o.r || 18, ry = r * 0.5, seed = o.seed || (Math.round(x) * 31 + 7);
    let sc = 1, a = 1;
    if (has(o, 't0')) { if (T < o.t0) return; const u = c01((T - o.t0) / 0.18); sc = rm() ? 1 : 1.5 - 0.5 * E.outCubic(u); a = c01((T - o.t0) / 0.08); }
    g.save(); g.globalAlpha *= a * (has(o, 'alpha') ? o.alpha : 1); g.translate(x, y + ry * 0.75); g.scale(sc, sc);
    g.fillStyle = o.color || RED; blobPath(0, 0, r, ry, seed, 10, 0.2); g.fill();
    for (let i = 0; i < 3; i++) { const sx = (rnd(seed, 60 + i) - 0.5) * r * 3.2, sy = (rnd(seed, 70 + i) - 0.3) * ry * 2; circlePath(Math.max(1.2, r * 0.1 * (1 + rnd(seed, 80 + i))), sx, sy); g.fill(); }
    g.restore();
  };
  CK.track = function (x0, x1, y, o = {}) {
    if (x1 <= x0) return;
    const { p, a } = has(o, 'prog') ? { p: c01(o.prog), a: 1 } : prog(o.t0, o.dur || 0.6);
    if (p <= 0 || a <= 0) return;
    const yy = y + (has(o, 'dy') ? o.dy : 7);
    g.save(); g.globalAlpha *= a * (has(o, 'alpha') ? o.alpha : 0.85);
    strokePts(wob([[x0, yy], [x0 + (x1 - x0) * p, yy]], o.seed || 9, 0.9, 12), o.w || 6, o.color || RED);
    g.restore();
  };
  CK.flag = function (x, y, o = {}) {
    const s = o.s || 1, state = o.state || 'idle', H = (o.h || 116) * s, lift = state === 'held' ? 12 * s : 0;
    const top = y - H - lift, pw = 58 * s, ph = 40 * s;
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (state === 'held') { g.fillStyle = 'rgba(29,43,79,0.16)'; g.beginPath(); g.ellipse(x + 4, y + 3, 14 * s, 5 * s, 0, 0, TAU); g.fill(); }
    hline(x, y - lift, x, top, 4 * s, INK, 401, 0.4);
    const sway = state === 'held' ? 0.08 : 0;
    g.save(); g.translate(x, top + 2 * s); g.rotate(sway);
    g.beginPath(); g.moveTo(0, 0); g.quadraticCurveTo(pw * 0.55, ph * 0.12, pw, ph * 0.45); g.quadraticCurveTo(pw * 0.5, ph * 0.62, 0, ph); g.closePath();
    g.fillStyle = state === 'locked' ? INK : RED; g.fill(); g.lineWidth = 2.5 * s; g.strokeStyle = INK; g.stroke();
    if (state === 'locked') { g.strokeStyle = '#fff'; g.lineWidth = 3.5 * s; g.lineCap = 'round'; g.beginPath(); g.moveTo(pw * 0.18, ph * 0.5); g.lineTo(pw * 0.3, ph * 0.66); g.lineTo(pw * 0.52, ph * 0.3); g.stroke(); }
    g.restore();
    dot(x, top, 6.5 * s, INK);
    g.strokeStyle = INK; g.lineWidth = 2.5 * s; g.beginPath(); g.moveTo(x - 6 * s, y - lift + 1); g.lineTo(x + 6 * s, y - lift + 1); g.stroke();
    if (o.label) NB.write(o.label, x, top - 22 * s, { font: 'ui', size: UI.text(o.labelSize || 26), weight: 700, color: PENCIL, align: 'center' });
    g.restore();
    const hw = Math.max(UI.tap(o.hitW || 88), pw + 24 * s), hh = Math.max(UI.tap(88), y - top + 28 * s), cx = x + pw * 0.25, cy = (top + y) / 2;
    if (o.id) UI.target(o.id, cx, cy, hw, hh);
    return { x: cx - hw / 2, y: cy - hh / 2, w: hw, h: hh, cx, cy, tip: { x, y }, top };
  };

  /* ---------------- sticks, the bit ---------------- */
  CK.stick = function (x0, x1, y, o = {}) {
    const { p, a } = prog(o.t0, o.dur || 0.35), color = o.color || RED, w = o.w || 6, tk = o.tick || 13, seed = o.seed || Math.round(x0 * 7 + y);
    if (p <= 0 || a <= 0) return { x0, x1, y };
    g.save(); g.globalAlpha *= a * (has(o, 'alpha') ? o.alpha : 1);
    const xe = x0 + (x1 - x0) * p;
    strokePts(wob([[x0, y], [xe, y]], seed, 0.8, 10), w, color);
    if (o.ticks !== false) {
      strokePts([[x0, y - tk], [x0 + 0.6, y + tk]], w * 0.7, color);
      if (p >= 0.999) strokePts([[x1, y - tk], [x1 - 0.6, y + tk]], w * 0.7, color);
    }
    if (o.label !== undefined && o.label !== null && o.label !== '' && p >= 0.6) {
      const size = UI.text(o.size || 34), below = o.labelSide !== 'above';
      NB.write(String(o.label), (x0 + x1) / 2, y + (below ? 1 : -1) * (tk + size * 0.62), { font: 'hand', size, weight: 700, color, align: 'center', t0: has(o, 't0') ? o.t0 + (o.dur || 0.35) * 0.6 : undefined, dur: 0.2 });
    }
    g.restore();
    return { x0, x1, y, mid: (x0 + x1) / 2 };
  };
  CK.bit = function (x0, x1, y, o = {}) {
    const hh = o.h || 32, t0 = o.t0;
    NB.highlight(x0, y - hh / 2, Math.max(2, x1 - x0), hh, { t0, dur: o.dur || 0.3 });
    const label = o.label === undefined ? 'and a bit' : o.label;
    if (!label) return { x0, x1, y };
    const size = UI.text(o.size || 36), lx = has(o, 'lx') ? o.lx : (x0 + x1) / 2 + size * 0.4, ly = has(o, 'ly') ? o.ly : y + hh / 2 + size * 1.25;
    const { p, a } = prog(t0 === undefined ? undefined : t0 + 0.25, 0.25);
    if (p > 0 && a > 0) strokePts(trimPts(wob([[lx - size * 0.15, ly - size * 0.62], [(x0 + x1) / 2, y + hh / 2 + 4]], 501, 0.8, 6), p), 3, RED, a);
    NB.note(label, lx, ly, -0.03, { t0: t0 === undefined ? undefined : t0 + 0.35, size });
    return { x0, x1, y, label: { x: lx, y: ly } };
  };

  /* ---------------- diameter, radius ---------------- */
  function segLabel(text, x, y, o, color) {
    if (text === null || text === undefined || text === '') return;
    NB.write(String(text), x, y, { font: 'hand', size: UI.text(o.size || 38), weight: 700, color, align: 'center', t0: o.t0 === undefined ? undefined : o.t0 + (o.dur || 0.4) * 0.7, dur: 0.2 });
  }
  CK.diameter = function (cx, cy, R, angle = 0, o = {}) {
    const c = Math.cos(angle), s = Math.sin(angle), color = o.color || RED, w = o.w || 5, inset = o.inset || 0;
    const p0 = [cx - c * (R - inset), cy - s * (R - inset)], p1 = [cx + c * (R - inset), cy + s * (R - inset)];
    const { p, a } = prog(o.t0, o.dur || 0.4);
    const side = o.side || -1, nx = -s * side, ny = c * side, off = o.labelOff || UI.text(o.size || 38) * 0.75;
    const lab = { x: cx + c * R * 0.5 + nx * off, y: cy + s * R * 0.5 + ny * off };
    if (p > 0 && a > 0) {
      g.save(); g.globalAlpha *= a;
      strokePts(trimPts(wob([p0, p1], o.seed || 601, 0.7, 10), p), w, color);
      if (o.ticks !== false) {
        const tk = o.tick || 10;
        strokePts([[p0[0] - nx * tk, p0[1] - ny * tk], [p0[0] + nx * tk, p0[1] + ny * tk]], w * 0.7, color);
        if (p >= 0.999) strokePts([[p1[0] - nx * tk, p1[1] - ny * tk], [p1[0] + nx * tk, p1[1] + ny * tk]], w * 0.7, color);
      }
      if (o.center !== false) dot(cx, cy, o.dot || 6, INK);
      g.restore();
      segLabel(o.label === undefined ? 'd' : o.label, lab.x, lab.y, o, color);
    }
    return { p0: { x: p0[0], y: p0[1] }, p1: { x: p1[0], y: p1[1] }, c: { x: cx, y: cy }, label: lab };
  };
  CK.radius = function (cx, cy, R, angle = 0, o = {}) {
    const c = Math.cos(angle), s = Math.sin(angle), color = o.color || RED, w = o.w || 5, inset = o.inset || 0;
    const p1 = [cx + c * (R - inset), cy + s * (R - inset)];
    const { p, a } = prog(o.t0, o.dur || 0.35);
    const side = o.side || -1, nx = -s * side, ny = c * side, off = o.labelOff || UI.text(o.size || 38) * 0.75;
    const lab = { x: cx + c * R * 0.5 + nx * off, y: cy + s * R * 0.5 + ny * off };
    if (p > 0 && a > 0) {
      g.save(); g.globalAlpha *= a;
      strokePts(trimPts(wob([[cx, cy], p1], o.seed || 611, 0.7, 10), p), w, color);
      if (o.ticks !== false && p >= 0.999) { const tk = o.tick || 10; strokePts([[p1[0] - nx * tk, p1[1] - ny * tk], [p1[0] + nx * tk, p1[1] + ny * tk]], w * 0.7, color); }
      if (o.center !== false) dot(cx, cy, o.dot || 6, INK);
      g.restore();
      segLabel(o.label === undefined ? 'r' : o.label, lab.x, lab.y, o, color);
    }
    return { p0: { x: cx, y: cy }, p1: { x: p1[0], y: p1[1] }, c: { x: cx, y: cy }, label: lab };
  };

  /* ---------------- radius squares, the unit grid ---------------- */
  const CORNERS = { tl: [0, 0], tr: [1, 0], br: [1, 1], bl: [0, 1] };
  CK.radiusSquare = function (x, y, side, o = {}) {
    const corner = CORNERS[o.corner] ? o.corner : 'bl', [ux, uy] = CORNERS[corner];
    const P_ = (i, j) => [x + i * side, y + j * side];
    const C0 = P_(ux, uy), A_ = P_(1 - ux, uy), B_ = P_(ux, 1 - uy), D_ = P_(1 - ux, 1 - uy);   // centre corner, its neighbours, the far one
    let sc = 1, a = 1;
    if (has(o, 't0')) { if (T < o.t0) return null; const u = c01((T - o.t0) / 0.22); sc = rm() ? 1 : 1.12 - 0.12 * E.outCubic(u); a = c01((T - o.t0) / 0.1); }
    g.save(); g.globalAlpha *= a * (has(o, 'alpha') ? o.alpha : 1);
    const cxs = x + side / 2, cys = y + side / 2;
    g.translate(cxs, cys); g.scale(sc, sc); g.translate(-cxs, -cys);
    const poly = o.half === 'in' ? [C0, A_, B_] : o.half === 'out' ? [D_, A_, B_] : [C0, A_, D_, B_];
    if (!o.ghost) multiply(() => { g.globalAlpha *= 0.62; g.fillStyle = o.color || HI; pathPts(poly); g.fill(); });
    g.save(); g.setLineDash(o.half || o.ghost ? [9, 7] : []); g.lineWidth = 2.5; g.strokeStyle = o.ghost ? PENCIL : INK; g.strokeRect(x + 1.25, y + 1.25, side - 2.5, side - 2.5); g.restore();
    if (o.half && !o.ghost) {
      g.lineWidth = 3; g.strokeStyle = INK; g.lineJoin = 'round'; pathPts(poly); g.stroke();
      strokePts(wob([A_, B_], 701, 0.8, 10), 4, RED);                  // the cut, corner to corner
    }
    if (o.labels !== false) {
      const size = UI.text(o.size || 30), off = size * 0.7, color = o.labelColor || INK;
      // the two OUTER sides (away from the centre corner)
      const midH = [x + side / 2, uy ? y - off : y + side + off], midV = [ux ? x - off : x + side + off, y + side / 2];
      NB.write('r', midH[0], midH[1], { font: 'hand', size, weight: 700, color, align: 'center' });
      NB.write('r', midV[0], midV[1], { font: 'hand', size, weight: 700, color, align: 'center' });
    }
    g.restore();
    return { x, y, w: side, h: side, center: { x: C0[0], y: C0[1] }, far: { x: D_[0], y: D_[1] } };
  };
  const gridCache = new Map();
  const gridOf = r => { let G = gridCache.get(r); if (!G) { G = Circle.gridCount(r); gridCache.set(r, G); } return G; };
  CK.grid = function (cx, cy, cell, r, o = {}) {
    const G = gridOf(r), n = Math.ceil(r), show = o.show || 'both', R = r * cell, dur = o.dur || 1.2;
    const anim = has(o, 't0') && !rm() && T < o.t0 + dur + 0.2;
    if (has(o, 't0') && T < o.t0) return G;
    const draw = (tt) => {
      for (const c of G.cells) {
        if (show === 'none' || (c.kind === 'whole' ? show === 'edge' : show === 'whole')) continue;
        if (tt !== null) { const d = Math.hypot(c.i + 0.5, c.j + 0.5) / (r + 1); if (tt < d) continue; }
        const x = c.i * cell, y = c.j * cell;
        if (c.kind === 'whole') { g.fillStyle = 'rgba(246,234,74,0.55)'; g.fillRect(x, y, cell, cell); }
        else {
          g.save(); g.beginPath(); g.rect(x, y, cell, cell); g.clip(); circlePath(R); g.clip();
          g.fillStyle = 'rgba(210,63,51,0.10)'; g.fillRect(x, y, cell, cell);
          g.strokeStyle = RED; g.lineWidth = 1.6; g.beginPath();
          for (let k = -cell; k < cell; k += 8) { g.moveTo(x + k, y + cell); g.lineTo(x + k + cell, y); }
          g.stroke(); g.restore();
        }
      }
      if (o.lines !== false) {
        g.strokeStyle = 'rgba(29,43,79,0.45)'; g.lineWidth = 1.3; g.beginPath();
        for (let i = -n; i <= n; i++) { g.moveTo(i * cell, -n * cell); g.lineTo(i * cell, n * cell); g.moveTo(-n * cell, i * cell); g.lineTo(n * cell, i * cell); }
        g.stroke();
      }
      if (o.circle !== false) { g.strokeStyle = INK; g.lineWidth = 3; circlePath(R - 1.5); g.stroke(); }
    };
    g.save(); g.translate(cx, cy); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (anim) { g.save(); draw(c01((T - o.t0) / dur) * 1.05); g.restore(); }
    else cached('grid', 'grid|' + r + '|' + cell + '|' + show + '|' + (o.lines !== false) + (o.circle !== false), -n * cell - 2, -n * cell - 2, 2 * n * cell + 4, 2 * n * cell + 4, () => draw(null));
    g.restore();
    return G;
  };

  /* ---------------- the lab table ---------------- */
  CK.table = function (x, y, rows, o = {}) {
    const size = UI.text(o.size || 30), hs = UI.text(o.headSize || 22), names = !!o.names;
    const heads = (names ? [o.nameHead || ''] : []).concat(o.heads || ['across', 'around', 'around ÷ across']);
    const fH = `700 ${hs}px ${UIF}`, fR = `700 ${size}px ${HAND}`;
    const cellText = (row, j) => { const keys = (names ? ['name'] : []).concat(['across', 'around', 'ratio']); const v = row[keys[j]]; return v === undefined || v === null ? '' : String(v); };
    g.save();
    const widths = heads.map((hd, j) => {
      g.font = fH; let w = g.measureText(hd).width;
      g.font = fR; for (const r of rows) w = Math.max(w, g.measureText(cellText(r, j)).width);
      return Math.max(w, (o.minCol || 0)) + size * 0.9;
    });
    g.restore();
    let W_ = widths.reduce((s, v) => s + v, 0);
    if (o.w && o.w > W_) { const extra = (o.w - W_) / widths.length; for (let j = 0; j < widths.length; j++) widths[j] += extra; W_ = o.w; }
    const headH = hs * 2.2, rowH = size * 1.6, nRows = Math.max(rows.length, o.slots || 0), Ht = headH + nRows * rowH;
    const cols = []; { let cx = x; for (const w of widths) { cols.push({ x: cx, w }); cx += w; } }
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(x + 3, y + 6, W_, Ht);
    g.fillStyle = '#fff'; g.fillRect(x, y, W_, Ht);
    g.fillStyle = PALN.cardLine; for (let i = 1; i < nRows; i++) g.fillRect(x, y + headH + i * rowH, W_, 1.5);
    g.strokeStyle = INK; g.lineWidth = 2.5; g.strokeRect(x, y, W_, Ht);
    g.beginPath(); g.moveTo(x, y + headH); g.lineTo(x + W_, y + headH); g.stroke();
    g.lineWidth = 1.5; g.beginPath(); for (let j = 1; j < cols.length; j++) { g.moveTo(cols[j].x, y); g.lineTo(cols[j].x, y + Ht); } g.stroke();
    g.font = fH; g.fillStyle = PENCIL; g.textAlign = 'center'; g.textBaseline = 'middle';
    heads.forEach((hd, j) => g.fillText(hd, cols[j].x + cols[j].w / 2, y + headH / 2));
    g.restore();
    const rowY = [];
    rows.forEach((row, i) => {
      const cy = y + headH + (i + 0.5) * rowH, t0 = has(row, 't0') ? row.t0 : undefined;
      rowY.push(cy);
      if (t0 !== undefined && T < t0) return;
      for (let j = 0; j < cols.length; j++) {
        const tj = t0 === undefined ? undefined : t0 + j * 0.3, last = j === cols.length - 1;
        if (last && o.hi !== false) NB.highlight(cols[j].x + 8, cy - size * 0.55, cols[j].w - 16, size * 1.1, { t0: tj === undefined ? undefined : tj + 0.35, dur: 0.3 });
        NB.write(cellText(row, j), cols[j].x + cols[j].w / 2, cy, { font: 'hand', size, weight: 700, color: row.color || INK, align: 'center', t0: tj, dur: 0.3 });
      }
    });
    return { x, y, w: W_, h: Ht, cols, rowY, headH, rowH };
  };

  /* ---------------- chips, levers, readouts ---------------- */
  CK.chip = function (text, x, y, o = {}) {
    const state = o.state || 'idle', size = UI.text(o.size || 36), f = `700 ${size}px ${HAND}`;
    g.save(); g.font = f; const tw = g.measureText(String(text)).width; g.restore();
    const h = Math.max(UI.tap(o.h || 64), size * 1.5), notch = h * 0.32, hole = h * 0.1, w = Math.max(o.w || 0, tw + notch + hole * 2 + size * 0.9);
    let dx = 0, lift = 0, rot = 0;
    if (state === 'held') { lift = 10; rot = -0.04; }
    if (state === 'wrong' && has(o, 't0') && !rm()) { const t = T - o.t0; if (t >= 0) dx = 12 * Math.sin(t * 38) * Math.exp(-t * 6); }
    const cx = x + dx, cy = y - lift;
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    g.translate(cx, cy); g.rotate(rot);
    const x0 = -w / 2, y0 = -h / 2, path = (ox, oy) => { g.beginPath(); g.moveTo(x0 + notch + ox, y0 + oy); g.lineTo(-x0 + ox, y0 + oy); g.lineTo(-x0 + ox, -y0 + oy); g.lineTo(x0 + notch + ox, -y0 + oy); g.lineTo(x0 + ox, oy); g.closePath(); };
    g.fillStyle = 'rgba(29,43,79,' + (state === 'held' ? 0.2 : 0.13) + ')'; path(3, state === 'held' ? 14 : 6); g.fill();
    path(0, 0); g.fillStyle = state === 'placed' ? '#fffbe0' : '#fff'; g.fill();
    g.lineJoin = 'round'; g.lineWidth = state === 'wrong' ? 4 : 3; g.strokeStyle = state === 'wrong' ? RED : INK; g.stroke();
    g.fillStyle = PAGE; circlePath(hole, x0 + notch * 0.75, 0); g.fill(); g.lineWidth = 2; g.strokeStyle = INK; g.stroke();
    const tx = x0 + notch + hole + (w - notch - hole - tw) / 2;
    if (state === 'placed') multiply(() => { g.globalAlpha *= 0.7; g.fillStyle = HI; g.fillRect(tx - 6, -size * 0.45, tw + 12, size * 0.9); });
    g.font = f; g.fillStyle = state === 'wrong' ? RED : INK; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(String(text), tx, 2);
    if (state === 'wrong') { g.strokeStyle = RED; g.lineWidth = 4; g.lineCap = 'round'; g.beginPath(); g.moveTo(tx - 4, size * 0.05); g.lineTo(tx + tw + 4, -size * 0.12); g.stroke(); }
    g.restore();
    const hw = Math.max(w, UI.tap(88)), hh = Math.max(h, UI.tap(64));
    if (o.id) UI.target(o.id, cx, cy, hw, hh);
    return { x: cx - hw / 2, y: cy - hh / 2, w: hw, h: hh, cx, cy, drawn: { w, h } };
  };
  CK.lever = function (x, y, w, value, o = {}) {
    const v = c01(value), r = UI.tap(o.knob || 60) / 2, th = o.trackH || 18, kx = x + v * w;
    g.save(); if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (o.disabled) g.globalAlpha *= 0.45;
    // what stands above the track (the knob, its value, the mark) decides where the caption goes
    const vs = UI.text(o.valueSize || 32), msz = UI.text((o.mark && o.mark.size) || 30), lsz = UI.text(o.labelSize || 26);
    let above = r + 6;
    if (has(o, 'valueText')) above = Math.max(above, r + vs * 1.35);
    if (o.mark) above = Math.max(above, th / 2 + 4 + 18 + msz * 1.25);
    if (o.label) NB.write(o.label, x, y - above - lsz * 0.75, { font: 'ui', size: lsz, weight: 700, color: PENCIL });
    g.fillStyle = 'rgba(29,43,79,0.10)'; g.fillRect(x + 2, y - th / 2 + 4, w, th);
    g.fillStyle = PALN.ruler || '#f3f1e6'; g.fillRect(x, y - th / 2, w, th);
    if (o.fill !== false) { g.fillStyle = o.fillColor || INK; g.globalAlpha *= 0.85; g.fillRect(x, y - th / 2, kx - x, th); g.globalAlpha /= 0.85; }
    g.lineWidth = 3; g.strokeStyle = INK; g.strokeRect(x, y - th / 2, w, th);
    const ls = UI.text(o.tickSize || 28);
    for (const tk of o.ticks || []) {
      const tx = x + c01(tk.at) * w;
      g.strokeStyle = INK; g.lineWidth = 2.5; g.beginPath(); g.moveTo(tx, y + th / 2); g.lineTo(tx, y + th / 2 + 12); g.stroke();
      if (tk.label !== undefined) NB.write(String(tk.label), tx, y + Math.max(th / 2 + 12, r + 4) + ls * 0.7, { font: 'hand', size: ls, weight: 700, color: INK, align: 'center' });
    }
    if (o.ends) {
      if (o.ends[0] !== undefined && o.ends[0] !== null) NB.write(String(o.ends[0]), x - r - 12, y, { font: 'hand', size: ls, weight: 700, color: INK, align: 'right' });
      if (o.ends[1] !== undefined && o.ends[1] !== null) NB.write(String(o.ends[1]), x + w + r + 12, y, { font: 'hand', size: ls, weight: 700, color: INK });
    }
    if (o.mark) {                                                      // a red-pen mark (the truth), dropped in at t0
      const m = o.mark, mx = x + c01(m.at) * w, { p, a } = prog(m.t0, 0.3);
      if (p > 0 && a > 0) {
        g.save(); g.globalAlpha *= a;
        const my = y - th / 2 - 4 - (1 - p) * 30, ms = 12, m_ = msz;
        g.fillStyle = m.color || RED; g.beginPath(); g.moveTo(mx, my); g.lineTo(mx - ms, my - ms * 1.5); g.lineTo(mx + ms, my - ms * 1.5); g.closePath(); g.fill();
        strokePts([[mx, y - th / 2], [mx, y + th / 2]], 4, m.color || RED);
        if (m.label !== undefined) NB.write(String(m.label), mx, my - ms * 1.5 - m_ * 0.65, { font: 'hand', size: m_, weight: 700, color: m.color || RED, align: 'center' });
        g.restore();
      }
    }
    // the knob
    g.fillStyle = 'rgba(29,43,79,0.16)'; circlePath(r, kx + 2, y + (o.held ? 9 : 5)); g.fill();
    g.fillStyle = o.held ? '#fbf3a0' : '#fff'; circlePath(r, kx, y - (o.held ? 4 : 0)); g.fill();
    g.lineWidth = 3.5; g.strokeStyle = INK; circlePath(r - 1.75, kx, y - (o.held ? 4 : 0)); g.stroke();
    g.lineWidth = 2.5; g.beginPath(); for (const d of [-0.28, 0, 0.28]) { g.moveTo(kx + d * r, y - r * 0.35 - (o.held ? 4 : 0)); g.lineTo(kx + d * r, y + r * 0.35 - (o.held ? 4 : 0)); } g.stroke();
    if (o.valueText !== undefined && o.valueText !== null) {
      NB.write(String(o.valueText), kx, y - r - vs * 0.75, { font: 'hand', size: vs, weight: 700, color: INK, align: 'center' });
    }
    g.restore();
    const hit = { x: x - r, y: y - r, w: w + 2 * r, h: 2 * r };
    if (o.id) UI.target(o.id, x + w / 2, y, hit.w, hit.h);
    return { x, y, w, x0: x, x1: x + w, value: v, knob: { x: kx, y, r }, hit, at: px => c01((px - x) / w), inside: (px, py) => px >= hit.x && px <= hit.x + hit.w && py >= hit.y && py <= hit.y + hit.h };
  };
  CK.readout = function (lines, x, y, o = {}) {
    const size = UI.text(o.size || 40), lh = size * 1.3, pad = size * 0.45, f = `700 ${size}px ${HAND}`;
    const L = lines.map(l => (typeof l === 'string' ? { text: l } : l));
    g.save(); g.font = f;
    let tw = 0; for (const l of L) tw = Math.max(tw, g.measureText(l.text).width);
    const w = Math.max(o.w || 0, tw + 2 * pad), h = L.length * lh + 2 * pad - (lh - size) * 0.6;
    if (has(o, 'alpha')) g.globalAlpha *= o.alpha;
    if (o.box !== false) {
      g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(x + 3, y + 6, w, h);
      g.fillStyle = '#fff'; g.fillRect(x, y, w, h);
      g.lineWidth = 2.5; g.strokeStyle = INK; g.strokeRect(x, y, w, h);
    }
    L.forEach((l, i) => {
      const ty = y + pad + size * 0.55 + i * lh;
      if (l.hi) { const lw_ = g.measureText(l.text).width; multiply(() => { g.globalAlpha *= 0.75; g.fillStyle = HI; g.fillRect(x + pad - 6, ty - size * 0.5, lw_ + 12, size); }); }
      g.font = f; g.fillStyle = l.color || INK; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(l.text, x + pad, ty);
    });
    g.restore();
    return { x, y, w, h };
  };

  /* ---------------- warming ---------------- */
  /* queue sprite bakes for what a beat will draw (fn draws it once, off screen): CK.warm(() => CK.slices(...)) */
  CK.warm = fn => { if (typeof prewarm === 'function' && prewarm.draw) prewarm.draw(fn); };
  CK.resetStats = () => { for (const k of Object.keys(STATS)) delete STATS[k]; };

  window.CK = CK;
})();
